import React, { useCallback, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNarrow } from '../../hooks/useNarrow';

/**
 * The dialog primitive every modal in this app should be built on top of.
 * Extracted from TemplateModal.tsx — the scroll lock, real focus trap,
 * Escape-to-close, focus restore on unmount, and busy-guarded requestClose
 * (an in-flight request can't be abandoned mid-flight, which used to leave
 * the caller with a stuck "Creating…" flag) all lived there first. New
 * modals get all of that for free instead of re-deriving it, and re-deriving
 * the bugs — no role="dialog", no aria-modal, no focus trap — that
 * TemplateModal's own header comment used to call out.
 *
 * Deliberately unopinionated about the panel's background, border and inner
 * layout: those vary per modal. `panelClassName`/`panelStyle` are the escape
 * hatch; the only look this primitive imposes is the DESIGN.md card radius
 * (12px, `rounded-xl`) and the 44×44 close button every dialog needs.
 */
export interface ModalProps {
  onClose: () => void;
  /** While true, both the backdrop click and Escape are ignored. */
  busy?: boolean;
  /** One of labelledBy/label gives the dialog its accessible name — pass
   *  whichever fits: an existing heading's id, or a literal string when
   *  there is no visible title. */
  labelledBy?: string;
  label?: string;
  width?: string;
  height?: string;
  panelClassName?: string;
  panelStyle?: React.CSSProperties;
  children: React.ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

// Stacked dialogs (BookCallModal over TemplateModal): every open Modal has
// its own document listener, so without this Escape would close all of them
// at once and two Tab traps would fight over focus. Only the topmost dialog —
// the last aria-modal one in the DOM, which is the one painted on top —
// answers keys; the ones under it wait their turn.
function isTopmostDialog(panel: HTMLElement): boolean {
  const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
  return dialogs.length === 0 || dialogs[dialogs.length - 1] === panel;
}

export const Modal: React.FC<ModalProps> = ({
  onClose,
  busy,
  labelledBy,
  label,
  width = 'min(95vw, 640px)',
  height = 'auto',
  panelClassName,
  panelStyle,
  children,
}) => {
  const narrow = useNarrow();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Never let a backdrop click or Escape cancel an in-flight request: it is
  // already running, and closing here strands the caller with no feedback.
  const requestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  // The close handler is read through a ref inside the effect below. Callers
  // almost always pass an inline arrow for onClose, so its identity changes on
  // every parent render; if the effect depended on it, it would re-run on
  // every keystroke typed into a field inside the dialog and `panel.focus()`
  // would yank focus out of that field each time (seen on the Team invite
  // modal: one character per click). Mount-only effect + ref = focus is
  // claimed once, Escape still calls the latest handler.
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    // Focus the panel itself rather than its first control, so a screen
    // reader announces the dialog's accessible name before its controls.
    panel?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (panel && !isTopmostDialog(panel)) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n) => n.offsetParent !== null,
      );
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      // Return focus where it came from; otherwise keyboard users are
      // dumped at the top of the document.
      previouslyFocused.current?.focus?.();
    };
    // Mount-only on purpose — see requestCloseRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={requestClose}
    >
      {/* A plain `flex items-center` on the scrollable overlay itself clips
          whichever end overflows first (the classic flexbox "safe center"
          problem) — content taller than the viewport, like the invite
          modal's app picker, made the title/close button AND the footer
          buttons unreachable, with no way to scroll to either. This inner
          wrapper only centers when the panel fits within min-height: 100%;
          once the panel grows past that, it just flows from the top of a
          normally-scrollable container, so every part of it stays reachable. */}
      {/* Below `sm` the panel is a bottom sheet: pinned to the bottom edge,
          full width, top corners only. */}
      <div className={`min-h-full flex justify-center ${narrow ? 'items-end pt-10' : 'items-center p-4'}`}>
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-label={label}
          tabIndex={-1}
          className={`relative w-full rounded-xl overflow-hidden focus:outline-none ${panelClassName || ''}`}
          style={{
            width: narrow ? '100%' : width,
            height,
            maxHeight: narrow ? 'calc(100dvh - 2.5rem)' : 'calc(100dvh - 2rem)',
            overflowY: 'auto',
            ...(narrow ? { borderRadius: '12px 12px 0 0', paddingBottom: 'env(safe-area-inset-bottom)' } : null),
            ...panelStyle,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="absolute top-3 right-3 z-10 flex items-center justify-center rounded-lg text-[#a3a3a3] hover:text-[#f5f5f5] hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8]"
            style={{ width: 44, height: 44 }}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
